/**
 * Flow Ultra JENIS 2 (Gmail ahli family, satu Gmail satu pelanggan) — tindakan admin.
 * Rekod akses (flowT2_akses) hanya boleh ditulis oleh server (rules menolak client),
 * jadi borang Add customer memanggil laluan admin flowSessionPilot. Kata laluan / kunci 2FA
 * TIDAK dihantar dari sini — ia disimpan dalam peti besi (projek sesaat-flow).
 */
import { auth } from '../firebase';

const API = import.meta.env.VITE_FLOW_SESSION_API || 'https://asia-southeast1-ai8saat.cloudfunctions.net/flowSessionPilot';
const MESEJ = {
    T2_AKAUN_SESAAT_TIADA: 'No SESAAT account uses this email. Ask the customer to sign up first, or check the spelling.',
    T2_AKSES_ROSAK: 'Some type 2 details are invalid (email, Google account, dates or credit limit).',
    // One Gmail = one customer (2026-10-04) — server: functions/flowT2Senarai.js sebabGmailTakBebas.
    T2_GMAIL_DAH_DIPAKAI: 'This Gmail is already given to another customer whose access is still running. Nothing was saved. Pick a different Gmail, or end that customer\'s access first — see Session Access → Type 2 customers.',
    T2_GMAIL_AKAUN_KONGSI: 'This Gmail is a shared Type 1 Gmail in Session Access. Nothing was saved. Pick a different Gmail for a Type 2 customer.',
    T2_SENARAI_TERLALU_BANYAK: 'Too many Type 2 records to check whether this Gmail is free. Nothing was saved. Contact the developer.',
    FORBIDDEN: 'Only an admin can do this.',
};

export async function tetapAksesJenis2({ email, gmail, startedAt, expiresAt, had }) {
    await auth.authStateReady();
    if (!auth.currentUser) throw new Error('Please sign in again.');
    const r = await fetch(API + '/admin/t2-akses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + await auth.currentUser.getIdToken() },
        body: JSON.stringify({ email, gmail, kiraDari: startedAt.getTime(), endAt: expiresAt.getTime(), had, enabled: true }),
        credentials: 'omit', cache: 'no-store', signal: AbortSignal.timeout(30000),
    }).catch(() => null);
    if (!r) throw new Error('Could not reach the Flow server. Check your connection and try again.');
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(MESEJ[data?.error] || `Type 2 setup failed (${data?.error || 'HTTP ' + r.status}).`);
    return data;
}

// Popup Edit Subscription → "Count credits from" (FlowKiraDari.jsx). Reset + kira semula dari tarikh pilihan.
// Jawapan: { kiraDari, dahGuna, had, cara: 'siap' (dikira serta-merta) | 'tunggu' (perlu bacaan browser pelanggan) }.
const MESEJ_KIRA = {
    T2_AKSES_ROSAK: 'That date is not valid (it must be in the past and before the access end date).',
    T2_BUKAN_JENIS2: 'This customer has no Type 2 access record.',
    T2_SIBUK: "The customer's credits were being read at the same moment. Press it again.",
    T2_BARIS_TERLALU_BANYAK: 'Too many credit records to recount at once. Contact the developer.',
    FORBIDDEN: 'Only an admin can do this.',
    NOT_FOUND: 'Not saved — the server update is not deployed yet.',
};
export async function tetapKiraDariJenis2({ uid, kiraDari }) {
    if (typeof uid !== 'string' || !uid || !Number.isSafeInteger(kiraDari) || kiraDari <= 0) throw new Error('Not saved — invalid customer or date.');
    await auth.authStateReady();
    if (!auth.currentUser) throw new Error('Please sign in again.');
    const r = await fetch(API + '/admin/t2-kira-dari', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + await auth.currentUser.getIdToken() },
        body: JSON.stringify({ uid, kiraDari }),
        credentials: 'omit', cache: 'no-store', signal: AbortSignal.timeout(30000),
    }).catch(() => null);
    if (!r) throw new Error('Could not reach the Flow server. Check your connection and try again.');
    const data = await r.json().catch(() => ({}));
    if (!r.ok || !Number.isSafeInteger(data?.kiraDari)) throw new Error(MESEJ_KIRA[data?.error] || `Not saved (${data?.error || 'HTTP ' + r.status}).`);
    return data;
}

// Session Access → "Type 2 customers" (FlowT2Senarai.jsx, 2026-10-04). Both calls are read-only.
// The server update may not be deployed yet → 404 → said plainly, not shown as an empty list.
async function bacaAdmin(path, timeoutMs) {
    await auth.authStateReady();
    if (!auth.currentUser) throw new Error('Please sign in again.');
    const r = await fetch(API + '/admin/' + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + await auth.currentUser.getIdToken() },
        body: '{}', credentials: 'omit', cache: 'no-store', signal: AbortSignal.timeout(timeoutMs),
    }).catch(() => null);
    if (!r) throw new Error('Could not reach the Flow server. Check your connection and try again.');
    const data = await r.json().catch(() => ({}));
    if (r.status === 404) throw new Error('This needs the server update, which is not deployed yet.');
    if (!r.ok) throw new Error(data?.error === 'FORBIDDEN' ? 'Only an admin can see this.' : `The Flow server refused the request (${data?.error || 'HTTP ' + r.status}).`);
    return data;
}
// { baris: [{ uid, sesaatEmail, gmail, had, endAt, kiraDari, enabled, aktif, dahGuna, kreditPada, berganda, vaultId }], terpotong, rosak }
export async function muatSenaraiJenis2() {
    const data = await bacaAdmin('t2-senarai', 30000);
    if (!Array.isArray(data?.baris)) throw new Error('The Flow server sent an unexpected answer for the Type 2 list.');
    return data;
}
// { semak: { [uid]: { status: 'saved' | 'missing' | 'mismatch' | 'incomplete' | 'unknown', ada2fa } } }
// Status only — the password itself never reaches this page. Up to ~27 s on the server.
export async function semakVaultJenis2() {
    const data = await bacaAdmin('t2-vault', 40000);
    if (!data?.semak || typeof data.semak !== 'object') throw new Error('The Flow server sent an unexpected answer for the vault check.');
    return data;
}

// Admin "count credits" (robot GenLogin). Both write to the server; the robot on the PC does the reading.
// t2-senarai now also returns { robot: {[uid]:{profileId,status,reason,pada}}, job:{status,mula,siap,total,scope} }.
const MESEJ_KIRA_ROBOT = {
    T2_AKSES_ROSAK: 'Not saved — invalid customer or GenLogin profile ID (digits only).',
    T2_BUKAN_JENIS2: 'This customer has no active Type 2 access.',
    T2_ROBOT_TIADA_PROFIL: 'Set a GenLogin profile ID for this customer first.',
    T2_ROBOT_TIADA_SASARAN: 'No Type 2 customers have a GenLogin profile ID set yet.',
    T2_SENARAI_TERLALU_BANYAK: 'Too many Type 2 records. Contact the developer.',
    FORBIDDEN: 'Only an admin can do this.',
    NOT_FOUND: 'This needs the server update, which is not deployed yet.',
};
async function tulisAdmin(path, body, salah) {
    await auth.authStateReady();
    if (!auth.currentUser) throw new Error('Please sign in again.');
    const r = await fetch(API + '/admin/' + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + await auth.currentUser.getIdToken() },
        body: JSON.stringify(body), credentials: 'omit', cache: 'no-store', signal: AbortSignal.timeout(30000),
    }).catch(() => null);
    if (!r) throw new Error('Could not reach the Flow server. Check your connection and try again.');
    const data = await r.json().catch(() => ({}));
    if (r.status === 404) throw new Error(salah.NOT_FOUND);
    if (!r.ok || data?.success !== true) throw new Error(salah[data?.error] || `Not saved (${data?.error || 'HTTP ' + r.status}).`);
    return data;
}
// Set the GenLogin profile ID the robot opens for this customer. '' clears it.
export async function tetapProfilJenis2({ uid, profileId }) {
    if (typeof uid !== 'string' || !uid) throw new Error('Not saved — invalid customer.');
    return tulisAdmin('t2-profil', { uid, profileId: String(profileId ?? '').trim() }, MESEJ_KIRA_ROBOT);
}
// Start the robot run. scope 'all' = every customer with a profile ID; 'satu' = one customer (Retry).
export async function mulaKiraJenis2({ scope = 'all', uid } = {}) {
    const body = scope === 'satu' ? { scope: 'satu', uid } : { scope: 'all' };
    return tulisAdmin('t2-kira-mula', body, MESEJ_KIRA_ROBOT);
}
